import {
  Flex,
  Image,
  useColorModeValue,
  Text,
  Skeleton,
  Heading,
  Center,
  Badge,
  HStack,
  Avatar,
  AvatarGroup,
  useDisclosure,
  useMediaQuery
} from '@chakra-ui/react'
import styled from '@emotion/styled'

import React, { useState } from 'react'

import { Property } from 'generated/graphql'
import { amountFormatter, formatPropertyTitle } from 'utils'
import { images } from 'theme'
import moment from 'moment'
import { FavoriteProperty, PropertyStatInfos, SlideShowModal } from '..'
import { VscCalendar } from 'react-icons/vsc'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useTranslation } from 'react-i18next'

// import Swiper core and required modules
import SwiperCore, { Navigation, Thumbs } from 'swiper/core'
import { useGeolocation } from 'hooks'

const CardContainer = styled(Flex)`
  width: 100%;

  .front,
  .back {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    transition: all 0.3s ease-in-out 0s;
  }
`

// install Swiper modules
SwiperCore.use([Navigation, Thumbs])

type Props = {
  property: Property
  loading?: boolean
  onClick?: () => void
}
const PropertyCard = React.forwardRef<HTMLDivElement, Props>(
  ({ property, loading, onClick }, ref) => {
    const themeMode = useColorModeValue('white', 'gray.700')
    const { currency } = useGeolocation()
    const [thumbsSwiper] = useState<SwiperCore | null | undefined>(null)
    const borderColor = useColorModeValue('gray.100', 'gray.700')
    const priceColor = useColorModeValue('blue.600', 'blue.100')
    const { isOpen, onClose, onOpen } = useDisclosure()
    const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
    const { t, i18n } = useTranslation()
    const tanslator = i18n as any
    const lang = tanslator.language as string
    return (
      <Skeleton isLoaded={!loading} ref={ref}>
        <CardContainer position="relative" background={themeMode} mb={10}>
          <Flex
            direction="column"
            className="front"
            borderRadius="xl"
            onClick={onClick}
            overflow="hidden"
            zIndex={24}
          >
            <Center
              bgRepeat="no-repeat"
              bgSize="40%"
              bgPosition="center"
              bgImage={images.logo_purple}
              height="100%"
              maxH={210}
              bgColor={themeMode}
              position="relative"
              flexDirection="column"
              p={1}
            >
              <Flex borderRadius="xl" width="100%" overflow="hidden" flex={1}>
                <Swiper
                  slidesPerView={1}
                  autoHeight={true}
                  spaceBetween={0}
                  thumbs={{ swiper: thumbsSwiper }}
                  className="cardSwiper"
                  pagination={{
                    clickable: true,
                    dynamicBullets: true
                  }}
                >
                  {property?.photos?.map((photo) => (
                    <SwiperSlide key={photo?.id}>
                      <Skeleton
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        minH="100%"
                        isLoaded={!loading}
                      >
                        <Image
                          flex={1}
                          objectFit="cover"
                          alt="nsoko property, property image"
                          maxH="100%"
                          minH={200}
                          fallback={<Skeleton width="100%" borderRadius="xl" height={210} />}
                          fallbackSrc={photo?.formats?.thumbnail?.url}
                          src={
                            photo?.formats?.small?.url ?? photo?.formats?.medium?.url ?? photo?.url
                          }
                        />
                      </Skeleton>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Flex>
            </Center>

            <Flex
              direction="column"
              justify="flex-start"
              align="flex-start"
              background={themeMode}
              py={4}
              px={5}
            >
              <Skeleton isLoaded={!loading}>
                <Flex align="center">
                  <Heading fontSize="lg" color={priceColor} mr={2} as="h5">
                    {amountFormatter(property?.price as number, currency)}
                  </Heading>
                  <Badge>{(property?.rental_term as any)?.[`name_${lang}`]}</Badge>
                  {moment().diff(moment(property?.published_at), 'days') < 2 && (
                    <Badge fontSize="xs" colorScheme="green">
                      new
                    </Badge>
                  )}
                </Flex>
              </Skeleton>
              <Center mt={2}>
                <VscCalendar />
                <Text mx={1} fontWeight="semibold" borderRadius={0} variant="subtle" fontSize="xs">
                  Disponible le {moment(property?.occupation_date).format('DD MMMM YYYY')}
                </Text>
              </Center>

              <Flex my={2} color="gray.500" justify="space-between">
                <Text fontSize="xs">{formatPropertyTitle(property as Property, t, lang)}</Text>
              </Flex>

              {property?.suburb?.name && (
                <Skeleton isLoaded={loading}>
                  <Badge colorScheme="green">
                    {property?.suburb?.name}, {property?.suburb?.city?.name}
                  </Badge>
                </Skeleton>
              )}
              <HStack spacing={2} fontSize="xs">
                <PropertyStatInfos propertyData={property as Property} loading={loading} />
              </HStack>
            </Flex>
          </Flex>
          <FavoriteProperty
            position="absolute"
            top={0}
            bg={borderColor}
            borderRadius="full"
            width={7}
            height={7}
            zIndex={44}
            right={0}
            fontSize="xs"
            m={6}
            propertyData={property as Property}
          />

          <Center m={4} onClick={onOpen} top={0} zIndex={44} position="absolute" left={0}>
            <AvatarGroup size="md" max={isTabletOrMobile ? 1 : 2}>
              {property?.photos?.map((photo) => (
                <Avatar
                  key={photo?.id}
                  flex={1}
                  objectFit="cover"
                  src={photo?.formats?.thumbnail?.url}
                  alt={photo?.alternativeText as string}
                />
              ))}
            </AvatarGroup>
          </Center>
        </CardContainer>
        <SlideShowModal
          isOpen={isOpen}
          onClose={onClose}
          property={property as Property}
          loading={loading}
        />
      </Skeleton>
    )
  }
)
export default PropertyCard
